import { useCallback, useEffect, useRef, useState } from 'react';
import firebase from 'firebase/app';
import 'firebase/firestore';

interface TemplateData {
  id: string;
  title: string;
  status: string;
  tasks: unknown[];
  messages: { other?: unknown[] };
  supportingActors: Record<string, unknown>;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
}

export function useTemplatePreview(messages: Message[]) {
  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchedIdRef = useRef<string | null>(null);

  // Extract template ID from the latest assistant message
  const extractTemplateId = useCallback((msgs: Message[]): string | null => {
    // Walk messages in reverse to find the latest template action
    for (let i = msgs.length - 1; i >= 0; i--) {
      const msg = msgs[i];
      if (msg.role !== 'assistant') continue;

      try {
        const data = JSON.parse(msg.content);
        // Check for template creation/import/clone actions
        if (data.templateId) return data.templateId;
        if (data.newTemplateId) return data.newTemplateId;
      } catch {
        // Not JSON — skip
      }
    }
    return null;
  }, []);

  // Fetch template from Firestore
  const fetchTemplate = useCallback(async (templateId: string) => {
    if (templateId === lastFetchedIdRef.current) return; // Already loaded

    setIsLoading(true);
    try {
      const doc = await firebase.firestore()
        .collection('experienceTemplatesV1')
        .doc(templateId)
        .get();

      if (doc.exists) {
        const data = doc.data()!;
        setTemplate({
          id: doc.id,
          title: data.title ?? 'Untitled',
          status: data.status ?? 'DRAFT',
          tasks: data.tasks ?? [],
          messages: data.messages ?? { other: [] },
          supportingActors: data.supportingActors ?? {},
        });
        lastFetchedIdRef.current = templateId;
      }
    } catch (error) {
      console.error('Failed to fetch template for preview:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Watch for template actions in messages
  useEffect(() => {
    const templateId = extractTemplateId(messages);
    if (templateId) {
      fetchTemplate(templateId);
    }
  }, [messages, extractTemplateId, fetchTemplate]);

  // Also set up a Firestore listener for real-time updates when template is being modified
  useEffect(() => {
    if (!lastFetchedIdRef.current) return;

    const unsubscribe = firebase.firestore()
      .collection('experienceTemplatesV1')
      .doc(lastFetchedIdRef.current)
      .onSnapshot((doc) => {
        if (doc.exists) {
          const data = doc.data()!;
          setTemplate({
            id: doc.id,
            title: data.title ?? 'Untitled',
            status: data.status ?? 'DRAFT',
            tasks: data.tasks ?? [],
            messages: data.messages ?? { other: [] },
            supportingActors: data.supportingActors ?? {},
          });
        }
      });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastFetchedIdRef.current]);

  const clearPreview = useCallback(() => {
    setTemplate(null);
    lastFetchedIdRef.current = null;
  }, []);

  return { template, isLoading, clearPreview };
}
